import { type OperationVariables } from '@apollo/react-common';
import { type GraphQLTaggedNode } from 'react-relay';
import { type FetchQueryFetchPolicy } from 'relay-runtime';

import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { isRelayEnabled } from './isRelayEnabled.ts';
import { fetchQueryWithErrorPolicy } from './fetchQuery.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { mapFetchPolicyForQuery, type QueryInteropFetchPolicy } from './mapFetchPolicyForQuery.ts';
import {
	syncQueryCacheBridge,
	type QueryCacheBridgeSource,
} from './cacheBridge/queryCacheBridgeSync.ts';
import { getRelayOperationName } from './relayOperation.ts';

export type QueryInteropSupportedResult<TData> = {
	data: TData;
	error?: ApolloErrorInterop;
};

export type QueryInteropSupportedArgs<
	_TData,
	TVariables extends Record<string, any> = OperationVariables,
> = {
	query: GraphQLTaggedNode;
	variables?: TVariables;
	errorPolicy?: 'none' | 'ignore' | 'all';
	fetchPolicy?: QueryInteropFetchPolicy;
	context?: Record<string, any>;
	/**
	 * Ensures that the interop in Apollo build variant uses the AGG client
	 */
	interopApolloAggClient?: boolean;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export function queryInterop<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: QueryInteropSupportedArgs<TData, TVariables>,
): Promise<QueryInteropSupportedResult<TData>> {
	const operationName = getRelayOperationName(params.query);
	const source: QueryCacheBridgeSource = isRelayEnabled(params.__callerPackage__, {
		operationName,
		phase: 'preload',
	})
		? 'relay'
		: 'apollo';

	return queryInteropWithBridge<TData, TVariables>(params, source);
}

async function queryInteropWithBridge<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	params: QueryInteropSupportedArgs<TData, TVariables>,
	source: QueryCacheBridgeSource,
): Promise<QueryInteropSupportedResult<TData>> {
	const result =
		source === 'relay'
			? await queryInteropRelay<TData, TVariables>(params)
			: await queryInteropApollo<TData, TVariables>(params);

	// Keep the original query result contract intact, then mirror successful
	// results into the secondary cache as best-effort bridge work.
	syncQueryCacheBridge({
		source,
		error: result.error,
		query: params.query,
		variables: params.variables,
		data: result.data,
		fetchPolicy: params.fetchPolicy,
		useAggClient: params.interopApolloAggClient,
	});

	return result;
}

async function queryInteropRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	params: QueryInteropSupportedArgs<TData, TVariables>,
): Promise<QueryInteropSupportedResult<TData>> {
	const relayEnv = window.GLOBAL_RELAY_ENV;

	if (!relayEnv) {
		// eslint-disable-next-line no-console
		console.warn(
			`[Relay migration] Missing GLOBAL_RELAY_ENV for queryInterop; falling back to Apollo for package ${params.__callerPackage__}.`,
		);
		return queryInteropApollo<TData, TVariables>(params);
	}

	const relayFetchPolicy: FetchQueryFetchPolicy = mapFetchPolicyForQuery(params.fetchPolicy);

	const result = await fetchQueryWithErrorPolicy<{ response: TData; variables: TVariables }>(
		relayEnv,
		params.query,
		params.variables ?? ({} as TVariables),
		{ fetchPolicy: relayFetchPolicy, networkCacheConfig: { metadata: params.context } },
		{ errorPolicy: params.errorPolicy },
	).toPromise();

	return {
		data: result?.data as TData,
		error: result?.error,
	};
}

async function queryInteropApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>({
	query,
	fetchPolicy,
	...rest
}: QueryInteropSupportedArgs<TData, TVariables>): Promise<QueryInteropSupportedResult<TData>> {
	const client = rest.interopApolloAggClient
		? (window['GLOBAL_APOLLO_AGG_CLIENT'] ?? (window as any)['MOCK_GATEWAY_APOLLO_CLIENT'])!
		: window['GLOBAL_APOLLO_CLIENT']!;

	// `client.query` only accepts the narrower `FetchPolicy` (no `cache-and-network`/`standby`).
	// Map the watch-only values to the closest one-shot equivalent.
	const oneShotFetchPolicy =
		fetchPolicy === 'cache-and-network' || fetchPolicy === 'standby' ? 'cache-first' : fetchPolicy;

	const result = await client.query({
		...rest,
		fetchPolicy: oneShotFetchPolicy,
		query: toDocumentNode(query),
	});

	return {
		data: result.data,
		error: result.errors?.length
			? new ApolloErrorInterop({ graphQLErrors: result.errors })
			: undefined,
	};
}

export function convertToApolloError(error: Error): ApolloErrorInterop {
	// TODO: Implement proper error conversion
	return error as ApolloErrorInterop;
}
